import type {
  PageViewData,
  PageDurationData,
  EventData,
  BatchEvent,
  AnalyticsResponse,
  PageViewResponse,
  EventResponse,
  BatchResponse,
} from './types';
import { apiClient } from '@libs/api/client';
import { API_BASE_URL } from '@constants/api-url';

const ANALYTICS_URL = '/analytics';

// 세션 ID 생성 및 관리
export const getSessionId = (): string => {
  const STORAGE_KEY = 'analytics_session_id';
  let sessionId = sessionStorage.getItem(STORAGE_KEY);

  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(STORAGE_KEY, sessionId);
  }

  return sessionId;
};

// 공통 데이터 생성
export const getCommonData = (userId?: string) => ({
  sessionId: getSessionId(),
  userId,
  timestamp: Date.now(),
  userAgent: navigator.userAgent,
  screenResolution: `${window.screen.width}x${window.screen.height}`,
});

// API 호출 헬퍼
const postAnalytics = async <T>(endpoint: string, data: unknown): Promise<AnalyticsResponse<T>> => {
  try {
    const response = await apiClient.post<AnalyticsResponse<T>>(`${ANALYTICS_URL}${endpoint}`, data);
    return response.data;
  } catch (error) {
    console.error('[Analytics] API Error:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
      },
    };
  }
};

// Beacon API를 사용한 전송 (페이지 이탈 시)
const sendBeacon = (endpoint: string, data: unknown): boolean => {
  const url = `${API_BASE_URL}${ANALYTICS_URL}${endpoint}`;
  const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
  return navigator.sendBeacon(url, blob);
};

// 페이지 조회 기록
export const trackPageView = async (
  data: Omit<PageViewData, keyof ReturnType<typeof getCommonData>>,
  userId?: string,
): Promise<AnalyticsResponse<PageViewResponse>> => {
  const payload: PageViewData = {
    ...getCommonData(userId),
    ...data,
  };

  return postAnalytics<PageViewResponse>('/page-view', payload);
};

// 페이지 체류 시간 기록 (Beacon 사용)
export const trackPageDuration = (
  data: Omit<PageDurationData, keyof ReturnType<typeof getCommonData>>,
  userId?: string,
): boolean => {
  const payload: PageDurationData = {
    ...getCommonData(userId),
    ...data,
  };

  return sendBeacon('/page-duration', payload);
};

// 이벤트 기록
export const trackEvent = async (
  data: Omit<EventData, keyof ReturnType<typeof getCommonData>>,
  userId?: string,
): Promise<AnalyticsResponse<EventResponse>> => {
  const payload: EventData = {
    ...getCommonData(userId),
    ...data,
  };

  return postAnalytics<EventResponse>('/event', payload);
};

// 이벤트 기록 (Beacon 사용 - 페이지 이동 시)
export const trackEventBeacon = (
  data: Omit<EventData, keyof ReturnType<typeof getCommonData>>,
  userId?: string,
): boolean => {
  const payload: EventData = {
    ...getCommonData(userId),
    ...data,
  };

  return sendBeacon('/event', payload);
};

// 배치 전송
export const trackBatch = async (events: BatchEvent[]): Promise<AnalyticsResponse<BatchResponse>> => {
  return postAnalytics<BatchResponse>('/batch', { events });
};

// 배치 전송 (Beacon 사용 - 페이지 이탈 시)
export const trackBatchBeacon = (events: BatchEvent[]): boolean => {
  return sendBeacon('/batch', { events });
};
